import {
  openModeOf,
  type ViewerContent,
  viewerContentSchema,
  WIKI_MARKDOWN_MAX,
} from "@esi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DiagramBlock, naturalSize } from "@/components/diagram/Diagram";
import { Markdown } from "@/components/markdown/Markdown";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { Notice } from "../components/Notice.tsx";
import { svgDataUrl } from "../conversation/artefacts/svg.ts";
import { attachmentDownloadHref, attachmentOpenHref } from "../conversation/attachments/api.ts";
import { attachmentKeys, clearAttachmentAccess } from "../conversation/attachments/queries.ts";
import { downloadHref, openHref } from "./download.ts";
import { projectKeys } from "./queries.ts";

/**
 * The in-app viewer of a text, Markdown or SVG source (openspec changes `open-in-browser`, design
 * §4, and `svg-preview`):
 * its text comes from the API's per-click view route, after the same checks as a download, and
 * stays in memory only while the dialog is open (no cache once it closes, never persisted).
 */
export type ViewerTarget = (
  | { kind: "document"; projectId: string; documentId: string; title: string }
  | { kind: "attachment"; conversationId: string; attachmentId: string; title: string }
) & {
  /** Where focus goes on close when the opening control is gone by then (a menu item). */
  returnFocus?: () => HTMLElement | null;
};

const SourceViewerContext = createContext<((target: ViewerTarget) => void) | null>(null);

/** Opens a source in the viewer; a no-op outside the provider (never a crash). */
export function useSourceViewer(): (target: ViewerTarget) => void {
  return useContext(SourceViewerContext) ?? noop;
}
const noop = () => {};

/** The per-click open route of a tab-mode source (PDF, PNG, JPEG). */
export const openHrefOf = (target: ViewerTarget) =>
  target.kind === "document"
    ? openHref(target.projectId, target.documentId)
    : attachmentOpenHref(target.conversationId, target.attachmentId);

/**
 * Opens a compatible source from a keyboard or pointer activation: a new tab on the open route
 * (a plain navigation, never a fetch) or the viewer. False when its type has no Open action.
 */
export function useOpenSource(): (target: ViewerTarget, mediaType: string) => boolean {
  const view = useSourceViewer();
  return useCallback(
    (target, mediaType) => {
      const mode = openModeOf(mediaType);
      if (mode === "tab") {
        window.open(openHrefOf(target), "_blank", "noopener,noreferrer");
      } else if (mode === "viewer") {
        view(target);
      }
      return mode !== null;
    },
    [view],
  );
}

/** One viewer for the whole app: every surface (Sources, citations, attachments) opens it. */
export function SourceViewerProvider({ children }: { children: ReactNode }) {
  // The last target stays while the dialog animates out, so it never collapses as it closes.
  const [state, setState] = useState<{ target: ViewerTarget | null; open: boolean }>({
    target: null,
    open: false,
  });
  const open = useCallback((target: ViewerTarget) => setState({ target, open: true }), []);
  return (
    <SourceViewerContext value={open}>
      {children}
      <SourceViewer
        target={state.target}
        open={state.open}
        onClose={() => setState((s) => ({ ...s, open: false }))}
      />
    </SourceViewerContext>
  );
}

const keyOf = (target: ViewerTarget) =>
  target.kind === "document"
    ? (["source-viewer", "document", target.projectId, target.documentId] as const)
    : (["source-viewer", "attachment", target.conversationId, target.attachmentId] as const);

async function fetchContent(target: ViewerTarget, signal: AbortSignal): Promise<ViewerContent> {
  const raw =
    target.kind === "document"
      ? await ok(
          await api.api.projects[":projectId"].documents[":documentId"].view.$get(
            { param: { projectId: target.projectId, documentId: target.documentId } },
            { init: { signal } },
          ),
        )
      : await ok(
          await api.api.conversations[":conversationId"].attachments[":attachmentId"].view.$get(
            { param: { conversationId: target.conversationId, attachmentId: target.attachmentId } },
            { init: { signal } },
          ),
        );
  return viewerContentSchema.parse(raw);
}

/**
 * The API refused this source (its level forbids it, or it is gone from view). A bare 404 (an
 * API without the route, during a canary) is not a refusal: Download still works there.
 */
const refused = (error: unknown) =>
  error instanceof ApiError && (error.status === 403 || (error.status === 404 && error.explained));

export function SourceViewer(props: {
  target: ViewerTarget | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { target } = props;
  return (
    <Dialog
      open={props.open && target !== null}
      onOpenChange={(open) => (open ? undefined : props.onClose())}
    >
      <DialogContent
        onCloseAutoFocus={(event) => {
          const back = target?.returnFocus?.();
          if (back?.isConnected) {
            event.preventDefault();
            back.focus();
          }
        }}
        showCloseButton={false}
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden rounded-xl p-0 sm:max-w-3xl"
      >
        <DialogHeader className="gap-1 border-b border-line px-6 pt-5 pb-4">
          <DialogTitle className="text-base leading-snug break-words">{target?.title}</DialogTitle>
          <DialogDescription className="text-xs text-fg-muted">
            {t("sourceViewer.description")}
          </DialogDescription>
        </DialogHeader>
        {/* Mounted while open only: closing drops the observer and, with gcTime 0, the text. */}
        {target ? <ViewerBody target={target} open={props.open} onClose={props.onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function ViewerBody({
  target,
  open,
  onClose,
}: {
  target: ViewerTarget;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const content = useQuery({
    queryKey: keyOf(target),
    queryFn: async ({ signal }) => {
      try {
        return await fetchContent(target, signal);
      } catch (error) {
        if (refused(error)) {
          // A refetch keeps a query's previous data beside its error: drop the text first.
          client
            .getQueryCache()
            .find({ queryKey: keyOf(target) })
            ?.setState({ data: undefined });
          // A refusal holds no content (the query keeps only the error) and refreshes the list
          // the source was opened from, so the stale row or chip goes too.
          if (target.kind === "attachment") {
            clearAttachmentAccess(client, target.conversationId);
            void client.invalidateQueries({
              queryKey: attachmentKeys.list(target.conversationId),
            });
          } else {
            void client.invalidateQueries({ queryKey: projectKeys.documents(target.projectId) });
          }
        }
        throw error;
      }
    },
    // Closed (animating out): no new request; with gcTime 0 the text goes once it unmounts.
    enabled: open,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const error = content.error;
  const code = error instanceof ApiError ? error.code : undefined;
  const denied = refused(error);
  const download =
    target.kind === "document"
      ? downloadHref(target.projectId, target.documentId)
      : attachmentDownloadHref(target.conversationId, target.attachmentId);
  return (
    <>
      <section
        data-esi-content="documents"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling region must be keyboard-scrollable (axe scrollable-region-focusable).
        tabIndex={0}
        aria-label={t("sourceViewer.content")}
        aria-busy={content.isPending}
        className={cn(
          "flex-1 overflow-y-auto px-6 py-5 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
          // Room for the text while it loads; a notice keeps its own height.
          content.isError ? undefined : "min-h-40",
        )}
      >
        {content.isPending ? (
          <div className="flex max-w-prose flex-col gap-2.5" role="status">
            <span className="sr-only">{t("sourceViewer.loading")}</span>
            <Skeleton className="h-5 w-2/5" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : content.data && !denied && content.data.mediaType === "image/svg+xml" ? (
          <SvgSource svg={content.data.content} title={content.data.title} />
        ) : content.data && !denied ? (
          // Text, and Markdown beyond what the renderer is built for (a wiki page's bound),
          // shows as written: parsing megabytes on the main thread would freeze the tab.
          content.data.mediaType === "text/plain" ||
          content.data.content.length > WIKI_MARKDOWN_MAX ? (
            <pre className="max-w-prose font-mono text-[13px] leading-relaxed break-words whitespace-pre-wrap text-fg">
              {content.data.content}
            </pre>
          ) : (
            <div className="max-w-prose">
              {/* The dialog title is the page's heading: the file's `#` sits under it. */}
              <Markdown
                markdown={content.data.content}
                headingBase={3}
                showLinkHost
                // A file that opens on its own title does not repeat the dialog's.
                omitTitleHeading={content.data.title}
              />
            </div>
          )
        ) : code === "too_large_to_view" ? (
          <Notice title={t("sourceViewer.tooLargeTitle")}>{t("sourceViewer.tooLarge")}</Notice>
        ) : code === "not_openable" ? (
          <Notice title={t("sourceViewer.notOpenableTitle")}>
            {t("sourceViewer.notOpenable")}
          </Notice>
        ) : denied ? (
          <Notice tone="warning">{t("sourceViewer.refused")}</Notice>
        ) : (
          <Notice tone="danger">
            {t("sourceViewer.failed", { reason: error?.message ?? "" })}
          </Notice>
        )}
      </section>
      <DialogFooter className="border-t border-line px-6 py-3">
        {denied ? null : (
          <Button variant="outline" asChild>
            <a
              href={download}
              target="_blank"
              rel="noopener noreferrer"
              referrerPolicy="no-referrer"
            >
              <Download aria-hidden="true" strokeWidth={1.75} />
              {t("sourceViewer.download")}
              <span className="sr-only"> {t("app.newTab")}</span>
            </a>
          </Button>
        )}
        <Button onClick={onClose}>{t("sourceViewer.close")}</Button>
      </DialogFooter>
    </>
  );
}

/**
 * An SVG source (openspec change `svg-preview`): the server checked it against the artefact
 * allowlist, DOMPurify cleans it again here, and it is only ever shown as an image of a `data:`
 * URL, which runs no script and loads nothing; never inline markup, a frame or a navigation.
 */
export function SvgSource({
  svg,
  title,
  win,
}: {
  svg: string;
  title: string;
  /** The window whose parser DOMPurify uses (tests pass a jsdom window, as for `svgDataUrl`). */
  win?: Window;
}) {
  const { t } = useTranslation();
  const url = useMemo(() => svgDataUrl(svg, win), [svg, win]);
  if (url === null) {
    return (
      <Notice title={t("sourceViewer.notOpenableTitle")}>{t("sourceViewer.notOpenable")}</Notice>
    );
  }
  // As artefacts draw an SVG: the column's width (an SVG without width or height would otherwise
  // fall back to 300 × 150); Expand shows it at its viewBox size, with zoom and pan (#379).
  return <DiagramBlock src={url} title={title} size={naturalSize(svg)} natural={false} />;
}
